import { Inject, Injectable, Injector } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { filter, interval, Subscription } from 'rxjs';

import { LogsService } from './http-client/logs.service';
import { LocalStorageService } from './local-storage.service';
import { ToastsService } from './toasts.service';

@Injectable({ providedIn: 'root' })
export class VersionCheckerService {
  isNewVersionAvailable: boolean = false;
  currentVersion?: string;

  intervalSource = interval(3 * 60 * 1000);
  intervalSubscription: Subscription;
  routerSubscription: Subscription;
  versionSubscription: Subscription;

  constructor(
    private swUpdate: SwUpdate,
    private router: Router,
    private localStorageService: LocalStorageService,
    private toastsService: ToastsService,
    private translate: TranslateService,
    @Inject(Injector) private readonly injector: Injector
  ) {
    this.checkForUpdate();
    this.checkCurrentVersion();
  }

  checkCurrentVersion() {
    this.currentVersion = this.localStorageService.load(LocalStorageService.APP_CURRENT_VERSION);
    this.versionSubscription?.unsubscribe();

    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.versionSubscription = this.swUpdate.versionUpdates.subscribe((x) => {
      if (x.type === 'NO_NEW_VERSION_DETECTED' && this.currentVersion !== x.version.hash) {
        if (this.currentVersion) {
          this.toastsService.showInfo(this.translate.instant('common.appVersionUpdated'));
        }
        this.injector
          .get(LogsService)
          .saveWebInfoLog(`New web app version instaled '${x.version.hash}'. Previous: '${this.currentVersion}'`);
        this.currentVersion = x.version.hash;
        this.localStorageService.store(LocalStorageService.APP_CURRENT_VERSION, x.version.hash);
        this.isNewVersionAvailable = true;
      }
      if (x.type === 'VERSION_DETECTED' || x.type === 'VERSION_READY') {
        this.isNewVersionAvailable = true;
      }
    });
  }

  checkForUpdate(): void {
    this.intervalSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();

    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.intervalSubscription = this.intervalSource.subscribe(async () => {
      try {
        this.isNewVersionAvailable = await this.swUpdate.checkForUpdate();
      } catch (error) {
        console.error('Failed to check for updates:', error);
      }
    });

    this.routerSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        filter(() => this.isNewVersionAvailable)
      )
      .subscribe({
        next: () => {
          this.swUpdate.activateUpdate();
          document.location.reload();
        },
        error: (error) => console.error('Failed to apply updates:', error),
      });
  }
}
